"use client";

import { Euro, FileDown } from "lucide-react";
import { formatCurrency } from "@/lib/formatting";
import type { DealPayments } from "@/lib/deals/payment/types";
import type { DealDocument } from "@/lib/deals/types";

interface PaymentSummaryProps {
  payment: DealPayments | null;
  role: "buyer" | "seller";
  className?: string;
  invoiceDocument?: DealDocument | null;
}

/**
 * PaymentSummary - Displays payment amounts relevant to the user's role
 * Buyer sees: total amount to pay
 * Seller sees: amount they will receive
 */
export function PaymentSummary({
  payment,
  role,
  className = "",
  invoiceDocument,
}: PaymentSummaryProps) {
  if (!payment) {
    return (
      <div className={`rounded-lg border border-zinc-200 bg-zinc-50 p-4 ${className}`}>
        <p className="text-sm text-zinc-500">Zahlungsinformationen werden noch erstellt.</p>
      </div>
    );
  }

  if (role === "buyer") {
    return (
      <div className={`rounded-lg border border-zinc-200 bg-white p-4 ${className}`}>
        <h3 className="font-semibold text-zinc-900 mb-4 flex items-center gap-2">
          <Euro className="w-5 h-5 text-zinc-600" />
          Zahlungsbetrag
        </h3>

        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-zinc-600">Kaufpreis</span>
            <span className="font-medium text-zinc-900">
              {formatCurrency(payment.total_amount)}
            </span>
          </div>

          {payment.buyer_commission_amount != null && payment.buyer_commission_amount > 0 && (
            <div className="flex justify-between items-center text-sm">
              <span className="text-zinc-500">zzgl. Käufer-Provision</span>
              <span className="text-zinc-600">
                {formatCurrency(payment.buyer_commission_amount)}
              </span>
            </div>
          )}

          <div className="border-t border-zinc-200 pt-3 mt-3">
            <div className="flex justify-between items-center">
              <span className="font-medium text-zinc-900">Gesamtbetrag</span>
              <span className="text-lg font-bold text-zinc-900">
                {formatCurrency(
                  (payment.total_amount ?? 0) + (payment.buyer_commission_amount ?? 0)
                )}
              </span>
            </div>
          </div>
        </div>

        {payment.bank_reference && (
          <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
            <p className="text-xs font-medium text-amber-800 uppercase tracking-wide mb-1">
              Verwendungszweck
            </p>
            <p className="text-sm font-mono text-amber-900">{payment.bank_reference}</p>
          </div>
        )}

        <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-xs text-blue-700">
            <strong>Kaufpreis:</strong> Bitte überweisen Sie den Kaufpreis gemäß Kaufvertrag direkt
            an den Verkäufer.
          </p>
          {payment.buyer_commission_amount != null && payment.buyer_commission_amount > 0 && (
            <p className="text-xs text-blue-700 mt-2">
              <strong>Provision:</strong> Die Vermittlungsprovision überweisen Sie bitte gemäß
              Rechnung an die Plattform.
            </p>
          )}
        </div>

        {invoiceDocument && (
          <div className="mt-3">
            <a
              href={invoiceDocument.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
            >
              <FileDown className="w-4 h-4" />
              {invoiceDocument.name}
            </a>
          </div>
        )}
      </div>
    );
  }

  // Seller view
  return (
    <div className={`rounded-lg border border-zinc-200 bg-white p-4 ${className}`}>
      <h3 className="font-semibold text-zinc-900 mb-4 flex items-center gap-2">
        <Euro className="w-5 h-5 text-zinc-600" />
        Ihre Auszahlung
      </h3>

      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <span className="font-medium text-zinc-900">Auszahlungsbetrag</span>
          <span className="text-lg font-bold text-primary">
            {formatCurrency(payment.seller_payout_amount ?? payment.total_amount)}
          </span>
        </div>
      </div>

      <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
        <p className="text-xs text-blue-700">
          Bitte bestätigen Sie unten den Eingang der Zahlung vom Käufer, sobald diese auf Ihrem
          Bankkonto eingegangen ist.
        </p>
      </div>
    </div>
  );
}
